import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { RaceRequestEvent, DriverPositionEvent } from 'src/models/signalr-events.model';

export type TripPhase =
  | 'idle'
  | 'in-queue'
  | 'receiving-request'
  | 'awaiting-accept-confirmation'
  | 'in-trip'
  | 'finishing';

export interface TripState {
  phase: TripPhase;
  queuePosition: number;
  queueSize: number;
  activeRequest: RaceRequestEvent | null;
  secondsRemaining: number;
  activeTrip: RaceRequestEvent | null;
}

const initialState: TripState = {
  phase: 'idle',
  queuePosition: 0,
  queueSize: 0,
  activeRequest: null,
  secondsRemaining: 0,
  activeTrip: null,
};

@Injectable({ providedIn: 'root' })
export class TripStateService {
  private _state$ = new BehaviorSubject<TripState>(initialState);
  public readonly state$ = this._state$.asObservable();

  public readonly phase$ = this.state$.pipe(map(s => s.phase), distinctUntilChanged());
  public readonly queuePosition$ = this.state$.pipe(map(s => s.queuePosition), distinctUntilChanged());
  public readonly queueSize$ = this.state$.pipe(map(s => s.queueSize), distinctUntilChanged());
  public readonly activeRequest$ = this.state$.pipe(map(s => s.activeRequest), filter(Boolean));
  public readonly activeTrip$ = this.state$.pipe(map(s => s.activeTrip), filter(Boolean));

  get currentState(): TripState { return this._state$.value; }

  enterQueue(): void {
    // Entering the queue means no request is pending the driver's decision, so
    // clear any lingering activeRequest. activeRequest$ is derived from a
    // BehaviorSubject and replays its current value to every new subscriber —
    // the info-queue page re-subscribes on each re-entry, so a stale request
    // (e.g. left over when OnAccept raced ahead of acceptRequest and beginTrip
    // no-opped) would be replayed as a phantom offer for an already-finished
    // trip. Runs from any phase because re-entry can happen mid-lifecycle.
    const current = this._state$.value;
    this._state$.next({
      ...current,
      phase: 'in-queue',
      activeRequest: null,
      secondsRemaining: 0,
    });
  }

  updateQueuePosition(pos: DriverPositionEvent): void {
    const current = this._state$.value;
    this._state$.next({
      ...current,
      queuePosition: pos.posicaoMotoristaFila,
      queueSize: pos.numeroCarrosFila,
    });
  }

  receiveRequest(request: RaceRequestEvent): void {
    this._transition(['in-queue', 'idle'], 'receiving-request', { activeRequest: request, secondsRemaining: 28 });
  }

  updateCountdown(seconds: number): void {
    const current = this._state$.value;
    this._state$.next({ ...current, secondsRemaining: seconds });
  }

  acceptRequest(): void {
    this._transition(['receiving-request'], 'awaiting-accept-confirmation');
  }

  declineRequest(): void {
    this._transition(['receiving-request', 'awaiting-accept-confirmation'], 'in-queue', {
      activeRequest: null,
      secondsRemaining: 0,
    });
  }

  beginTrip(trip: RaceRequestEvent): void {
    // Also allow 'receiving-request': the server's OnAccept can arrive before
    // the local acceptRequest() transition (SignalR send vs invoke-result
    // race). If beginTrip no-ops here, activeRequest is never cleared and gets
    // replayed later as a phantom offer. Always clears activeRequest.
    this._transition(['receiving-request', 'awaiting-accept-confirmation', 'in-queue'], 'in-trip', { activeTrip: trip, activeRequest: null });
  }

  endTrip(): void {
    this._transition(['in-trip', 'finishing'], 'idle', { activeTrip: null, activeRequest: null });
  }

  leaveQueue(): void {
    this._state$.next({ ...initialState });
  }

  reset(): void {
    this._state$.next({ ...initialState });
  }

  private _transition(
    from: TripPhase[],
    to: TripPhase,
    extraState: Partial<TripState> = {}
  ): void {
    const current = this._state$.value;
    if (!from.includes(current.phase)) {
      console.warn(`[TripState] Invalid transition: ${current.phase} → ${to} (expected one of: ${from.join(', ')})`);
      return;
    }
    this._state$.next({ ...current, phase: to, ...extraState });
  }
}
